import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

function fmtDateFromISO(iso) {
  if (!iso) return "___________";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y}`;
}

const tipTarifLabels = { ora: "pe ora", zi: "pe zi", luna: "pe luna", proiect: "per proiect" };
const termenPlataLabels = {
  factura: "la emiterea facturii",
  factura15: "in termen de 15 zile de la emiterea facturii",
  factura30: "in termen de 30 zile de la emiterea facturii",
  finalizare: "la finalizarea proiectului",
};

const ContractPreview = forwardRef(function ContractPreview({ data }, ref) {
  const isMobile = useIsMobile();
  const today = fmtDate(new Date());

  const sectionTitle = () => ({
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: "#2d2418",
    margin: "28px 0 12px 0",
    paddingBottom: "6px",
    borderBottom: "1px solid #e8e0d4",
    letterSpacing: "0.03em",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  });

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 8px 0",
    textAlign: "justify",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const indent = { ...p, paddingLeft: isMobile ? "12px" : "20px" };
  const bold = { fontWeight: 700, color: "#2d2418" };

  const fieldRow = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "2px 0",
    paddingLeft: isMobile ? "12px" : "20px",
  };

  const pfaName = data.pfaDenumire || "________________________";
  const pfaTitular = data.pfaTitular || "________________________";
  const ciVal = data.pfaCI ? formatCI(data.pfaCI) : "___ ___";

  function renderBeneficiar() {
    if (data.beneficiarPF) {
      const bciVal = data.beneficiarCI ? formatCI(data.beneficiarCI) : "___ ___";
      return (
        <>
          <p style={fieldRow}>Nume: <span style={bold}>{data.beneficiarNume || "________________________"}</span></p>
          <p style={fieldRow}>CNP: <span style={bold}>{data.beneficiarCNP || "________________________"}</span></p>
          <p style={fieldRow}>CI seria <span style={bold}>{bciVal}</span></p>
          <p style={fieldRow}>Domiciliul: <span style={bold}>{data.beneficiarAdresa || "________________________"}</span></p>
        </>
      );
    }
    return (
      <>
        <p style={fieldRow}>Denumire: <span style={bold}>{data.beneficiarDenumire || "________________________"}</span></p>
        <p style={fieldRow}>CUI: <span style={bold}>{data.beneficiarCUI || "________________________"}</span></p>
        {data.beneficiarRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.beneficiarRegCom}</span></p>}
        <p style={fieldRow}>Sediu social: <span style={bold}>{data.beneficiarSediu || "________________________"}</span></p>
        <p style={fieldRow}>Reprezentant legal: <span style={bold}>{data.beneficiarReprezentant || "________________________"}</span></p>
      </>
    );
  }

  function getBeneficiarName() {
    if (data.beneficiarPF) return data.beneficiarNume || "";
    return data.beneficiarDenumire ? `${data.beneficiarDenumire} prin ${data.beneficiarReprezentant || ""}` : "";
  }

  const tarifText = `${data.tarifSuma || "___"} ${data.tarifMoneda || "RON"} ${tipTarifLabels[data.tipTarif] || ""}`;

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "24px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          Contract de Colaborare / Prestari Servicii
        </h2>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: 0,
        }}>
          Nr. ______ din {today}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* I. PARTILE CONTRACTANTE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>I. PARTILE CONTRACTANTE</h3>
        <p style={p}><span style={bold}>1.1. Prestatorul:</span></p>
        <p style={fieldRow}>PFA <span style={bold}>{pfaName}</span>, CUI <span style={bold}>{data.pfaCUI || "________________________"}</span>, cu sediul profesional in <span style={bold}>{data.pfaSediu || "________________________"}</span></p>
        <p style={fieldRow}>Reprezentat prin <span style={bold}>{pfaTitular}</span>, CNP <span style={bold}>{data.pfaCNP || "________________________"}</span>, CI seria <span style={bold}>{ciVal}</span></p>

        <p style={{ ...p, color: "#8a7e70", fontStyle: "italic", margin: "16px 0" }}>si</p>

        <p style={p}><span style={bold}>1.2. Beneficiarul:</span></p>
        {renderBeneficiar()}

        <p style={{ ...p, fontStyle: "italic", marginTop: "16px" }}>
          au convenit incheierea prezentului contract de colaborare / prestari servicii in urmatoarele conditii:
        </p>
      </div>

      {/* II. OBIECTUL CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>II. OBIECTUL CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>2.1.</span> Prestatorul se obliga sa presteze in favoarea Beneficiarului urmatoarele servicii:
        </p>
        <p style={indent}>
          <span style={bold}>{data.obiectContract || "________________________"}</span>
        </p>
        <p style={p}>
          <span style={bold}>2.2.</span> Serviciile vor fi prestate in mod independent, Prestatorul nefiind subordonat Beneficiarului. Prestatorul isi organizeaza singur activitatea, programul de lucru si metodele de executie.
        </p>
        <p style={p}>
          <span style={bold}>2.3.</span> Prezentul contract NU constituie un raport de munca in sensul Codului Muncii.
        </p>
      </div>

      {/* III. DURATA CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>III. DURATA CONTRACTULUI</h3>
        {data.durataNedeterminata ? (
          <>
            <p style={p}>
              <span style={bold}>3.1.</span> Prezentul contract se incheie pe <span style={bold}>durata nedeterminata</span>, incepand cu data de <span style={bold}>{fmtDateFromISO(data.dataInceput)}</span>.
            </p>
            <p style={p}>
              <span style={bold}>3.2.</span> Oricare dintre parti poate denunta unilateral contractul cu un preaviz de <span style={bold}>{data.preavizReziliere || "30"} zile</span>.
            </p>
          </>
        ) : (
          <>
            <p style={p}>
              <span style={bold}>3.1.</span> Prezentul contract se incheie pe <span style={bold}>durata determinata</span>, incepand cu data de <span style={bold}>{fmtDateFromISO(data.dataInceput)}</span> si pana la data de <span style={bold}>{fmtDateFromISO(data.dataSfarsit)}</span>.
            </p>
            <p style={p}>
              <span style={bold}>3.2.</span> Contractul poate fi prelungit prin act aditional semnat de ambele parti.
            </p>
          </>
        )}
      </div>

      {/* IV. PRETUL SI MODALITATEA DE PLATA */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>IV. PRETUL SI MODALITATEA DE PLATA</h3>
        <p style={p}>
          <span style={bold}>4.1.</span> Pentru serviciile prestate, Beneficiarul va plati Prestatorului un tarif de <span style={bold}>{tarifText}</span>.
        </p>
        <p style={p}>
          <span style={bold}>4.2.</span> Plata se efectueaza prin transfer bancar, <span style={bold}>{termenPlataLabels[data.termenPlata] || "conform intelegerii partilor"}</span>.
        </p>
        <p style={p}>
          <span style={bold}>4.3.</span> Prestatorul va emite factura fiscala conform legislatiei in vigoare.
        </p>
        <p style={p}>
          <span style={bold}>4.4.</span> Intarzierea la plata atrage penalitati de 0.1% pe zi din suma datorata, incepand cu ziua urmatoare scadentei.
        </p>
      </div>

      {/* V. OBLIGATIILE PRESTATORULUI */}
      <h3 style={sectionTitle()}>V. OBLIGATIILE PRESTATORULUI</h3>
      {[
        "Sa presteze serviciile conform specificatiilor convenite in prezentul contract.",
        "Sa respecte termenele stabilite pentru executarea serviciilor.",
        "Sa informeze Beneficiarul cu privire la stadiul executarii serviciilor.",
        "Sa emita factura fiscala pentru serviciile prestate.",
        "Sa isi desfasoare activitatea in mod independent, conform statutului de PFA.",
      ].map((text, i) => (
        <p key={i} style={indent}>
          <span style={bold}>5.{i + 1}.</span> {text}
        </p>
      ))}

      {/* VI. OBLIGATIILE BENEFICIARULUI */}
      <h3 style={sectionTitle()}>VI. OBLIGATIILE BENEFICIARULUI</h3>
      {[
        "Sa puna la dispozitia Prestatorului toate informatiile si documentele necesare executarii serviciilor.",
        "Sa plateasca pretul convenit la termenele stabilite.",
        "Sa receptioneze serviciile prestate si sa comunice observatiile in termen de 5 zile lucratoare.",
      ].map((text, i) => (
        <p key={i} style={indent}>
          <span style={bold}>6.{i + 1}.</span> {text}
        </p>
      ))}

      {/* VII. INDEPENDENTA PRESTATORULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VII. INDEPENDENTA PRESTATORULUI</h3>
        <p style={indent}><span style={bold}>7.1.</span> Prestatorul declara ca desfasoara activitate independenta in baza autorizatiei PFA si nu se afla in raport de subordonare fata de Beneficiar.</p>
        <p style={indent}><span style={bold}>7.2.</span> Prestatorul este singur responsabil pentru plata impozitelor, contributiilor sociale (CAS, CASS) si a oricaror alte obligatii fiscale aferente veniturilor obtinute.</p>
        <p style={indent}><span style={bold}>7.3.</span> Prestatorul utilizeaza propriile resurse, echipamente si instrumente de lucru.</p>
      </div>

      {/* VIII. INCETAREA CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VIII. INCETAREA CONTRACTULUI</h3>
        <p style={p}><span style={bold}>8.1.</span> Contractul inceteaza:</p>
        {[
          data.durataNedeterminata ? null : "la expirarea duratei",
          "prin acordul scris al partilor",
          "prin reziliere pentru neexecutarea obligatiilor contractuale",
          `prin denuntare unilaterala cu preaviz de ${data.preavizReziliere || "30"} zile`,
        ].filter(Boolean).map((text, i) => (
          <p key={i} style={{ ...indent, paddingLeft: isMobile ? "20px" : "40px" }}>
            <span style={{ color: "#c45d3e", fontWeight: 600 }}>{"abcd"[i]})</span> {text}
          </p>
        ))}
        <p style={{ ...p, marginTop: "12px" }}>
          <span style={bold}>8.2.</span> In cazul rezilierii din culpa uneia dintre parti, partea in culpa datoreaza daune-interese celeilalte parti.
        </p>
      </div>

      {/* Dynamic sections */}
      {(() => {
        const roman = ["IX", "X", "XI", "XII", "XIII", "XIV", "XV"];
        let s = 0;
        let a = 9;
        const sections = [];

        if (data.includeNDA) {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="nda" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. CONFIDENTIALITATE</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Partile se obliga sa pastreze confidentialitatea tuturor informatiilor, documentelor si datelor la care au acces in executarea prezentului contract.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Obligatia de confidentialitate se mentine pe toata durata contractului si pentru o perioada de 2 ani de la incetarea acestuia.</p>
            </div>
          );
        }

        if (data.includeNonCompete) {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="noncompete" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. NON-CONCURENTA</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Pe durata contractului si pentru o perioada de <span style={bold}>{data.nonCompeteDurata || "6 luni"}</span> de la incetarea acestuia, Prestatorul se obliga sa nu presteze servicii similare pentru concurenti directi ai Beneficiarului{data.nonCompeteDomeniu ? ` in domeniul ${data.nonCompeteDomeniu}` : ""}.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Aceasta restrictie se aplica pe teritoriul Romaniei.</p>
            </div>
          );
        }

        {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="pi" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. PROPRIETATE INTELECTUALA</h3>
              {data.transferPI ? (
                <>
                  <p style={indent}><span style={bold}>{n}.1.</span> Drepturile de proprietate intelectuala asupra rezultatelor serviciilor prestate se transfera integral Beneficiarului la momentul platii integrale.</p>
                  <p style={indent}><span style={bold}>{n}.2.</span> Prestatorul cedeaza toate drepturile patrimoniale de autor asupra lucrarilor create in executarea prezentului contract.</p>
                </>
              ) : (
                <>
                  <p style={indent}><span style={bold}>{n}.1.</span> Drepturile de proprietate intelectuala asupra rezultatelor serviciilor prestate raman in proprietatea Prestatorului.</p>
                  <p style={indent}><span style={bold}>{n}.2.</span> Beneficiarul primeste o licenta neexclusiva de utilizare a rezultatelor, in scopul pentru care au fost create.</p>
                </>
              )}
            </div>
          );
        }

        {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="raspundere" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. RASPUNDERE</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Fiecare parte raspunde pentru prejudiciile cauzate celeilalte parti prin neindeplinirea sau indeplinirea necorespunzatoare a obligatiilor asumate.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Nici una dintre parti nu raspunde pentru neexecutarea obligatiilor cauzata de forta majora, conform legii.</p>
            </div>
          );
        }

        {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="litigii" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. LEGEA APLICABILA SI LITIGII</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Prezentul contract este guvernat de legea romana.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Litigiile se solutioneaza pe cale amiabila, iar in caz contrar de instantele judecatoresti competente de la sediul/domiciliul Prestatorului.</p>
            </div>
          );
        }

        {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="finale" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. DISPOZITII FINALE</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Orice modificare a prezentului contract se face prin act aditional semnat de ambele parti.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Prezentul contract s-a incheiat in 2 exemplare originale, cate unul pentru fiecare parte.</p>
            </div>
          );
        }

        return sections;
      })()}

      {/* Signatures */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid",
        breakInside: "avoid",
      }}>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            PRESTATOR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {pfaName}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            BENEFICIAR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {getBeneficiarName()}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ContractPreview;
